const { useMemo } = wp.element;
import Styled from "styled-components";
import { NekoTheme, NekoTooltip, NekoCheckbox, NekoIcon, NekoSelect, NekoOption, NekoInput, NekoButton } from '@neko-ui';
import { isPro } from '@app/settings';
import i18n from '../i18n';

const SectionButtonStyle = { minWidth: 0, padding: '0 4px' };
const infoMessage = isPro ? i18n.INFO_UNKNOWN_PRO : i18n.INFO_UNKNOWN;
const SectionFooterStyle = { marginTop: 25, borderTop: '1px solid lightgray', paddingTop: 15,
  display: 'flex', alignItems: 'center' };

const StyledUsedByField = Styled.div`
  cursor: pointer;

  .actions {
    display: flex;
    font-size: 10px;
    color: ${NekoTheme.blue};
    text-transform: uppercase;
    line-height: 10px;
    margin-left: 27px;
    opacity: 0.1;
    transition: opacity 0.2s ease-out;
  }

  .dashicons {
    font-size: 10px;
    margin-top: 2px;
    margin-bottom: -10px;
  }

  &:hover .actions {
    opacity: 1;
  }
`;

const ItemStatusIcon = ({ item }) => {
  const orange = NekoTheme.orange;
  const green = NekoTheme.green;
  const status = item?.info?.status ?? "ok";
  const isDisabled = item?.clean_style === 'never';
  const icon = (isDisabled || !item.info) ? null :
    <NekoIcon icon={status === 'ok' ? 'check-circle' : (status === 'warn' ? 'close' : 'alert')}
      color={status === 'ok' ? green : (status === 'warn' ? '#ba4300' : orange)}
      width={24} height={24} style={{ marginRight: 3 }}
    />
  return icon;
}

const NameField = ({ item, itemText, onChange = undefined, checked = undefined, name = undefined, isBusy = false }) => {
  const status = item?.info?.status ?? "ok";
  const isDisabled = item?.clean_style === 'never';
  const color = isDisabled ? '#cac4c4' : (status === 'ok' ? 'black' : (status === 'warn' ? '#ba4300' : 'black'));
  
  const checkbox = useMemo(() => {
    return (!item?.info || checked === undefined) ? null :
    <NekoCheckbox name={name ? name : item?.name} enabled={!isDisabled} checked={checked} onChange={onChange} isBusy={isBusy} />
  }, [name, item, checked, onChange, isDisabled, isBusy]);
  
  return <div style={{ display: 'flex', alignItems: 'center' }}>
    {checkbox}
    <span style={{ color }}>
      {itemText}
    </span>
  </div>;
}

const BulkUsedByButton = props => {
  const { itemsUsedBy, optionName, plugins, selectedItems,
    setSelectedUsedBy, toggleModal, updateOption, onRefresh } = props;
  const unknownSlug = 'unknown';
  const selectedItemNames = selectedItems.filter(v => v?.info?.custom || !v?.info?.usedBy.toLowerCase()).map(v => v.name);

  if (!selectedItemNames.length) { 
    return null;
  }

  const onAssociateWithPlugin = async (args) => {
    const selectedUsedByItems = [...args.items];
    const newItemsUsedBy = itemsUsedBy.filter(data => !selectedUsedByItems.includes(data.item));
    const plugin = plugins.find(data => data.slug === args.value);
    const addItem = args.value !== unknownSlug ? args.items.map(item => {
      return { item, name: plugin.name, slug: plugin.slug.replace(/-pro$/, '') };
    }) : [];
    await updateOption({ param: { name: optionName, value: [...newItemsUsedBy, ...addItem] },
      onSuccess: () => {
        onRefresh();
      }
    });
  }
  const onClick = () => {
    setSelectedUsedBy({ items: selectedItemNames, value: unknownSlug });
    const title = `Used By${selectedItemNames.length > 1 ? ` (${selectedItemNames.length} selected items)` : ''}`;
    toggleModal({ modal: 'usedBy', enable: true, onNext: onAssociateWithPlugin, title});
  }

  return <NekoButton icon="link" style={{...SectionButtonStyle, background: '#016699', padding: '0px 15px' }}
    onClick={onClick}>{i18n.INFO_BULK_USED_BY}</NekoButton>
}

const UsedByField = ({ item, itemName, itemsUsedBy, optionName, plugins,
  setSelectedUsedBy, toggleModal, updateOption, onRefresh }) => {
  const unknownSlug = 'unknown';
  const info = item?.info;
  const label = info?.usedBy;
  const status = info?.status;
  const custom = info?.custom;
  const isDisabled = item?.clean_style === 'never';

  let onPickPluginClick = null;
  if (!isDisabled) {
    const selectedValue = itemsUsedBy.find(data => data.item === itemName)?.slug ?? unknownSlug;
    const onAssociateWithPlugin = async (args) => {
      const selectedUsedByItems = [...args.items];
      const newItemsUsedBy = itemsUsedBy.filter(data => !selectedUsedByItems.includes(data.item));
      const plugin = plugins.find(data => data.slug === args.value);
      const addItem = args.value !== unknownSlug ? args.items.map(item => {
        return { item, name: plugin.name, slug: plugin.slug.replace(/-pro$/, '') };
      }) : [];
      await updateOption({
        param: { name: optionName, value: [...newItemsUsedBy, ...addItem] },
        onSuccess: () => {
          onRefresh();
        }
      });
    }
    onPickPluginClick = () => {
      setSelectedUsedBy({ items: [itemName], value: selectedValue });
      toggleModal({ modal: 'usedBy', enable: true, onNext: onAssociateWithPlugin, title: 'Used By'});
    };
  }

  return <StyledUsedByField onClick={onPickPluginClick}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <ItemStatusIcon item={item} />
      {status === 'ok' && <span style={{ color: isDisabled ? '#cac4c4' : '#3c434a' }}>{label}</span>}
      {status === 'warn' && <span style={{ color: '#ba4300', textDecoration: 'line-through' }}>{label}</span>}
      {status === 'n/a' && <a style={{ color: NekoTheme.orange }}>Unknown</a>}
      {custom && <span class="dashicons dashicons-tag"></span>}
    </div>
    {!!onPickPluginClick && <div className="actions">
      {status === 'n/a' && <span>Assign Plugin</span>}
      {status !== 'n/a' && <span>ReAssign Plugin</span>}
      <NekoIcon icon="pencil" style={{ marginLeft: 3 }} height="10" />
    </div>}
  </StyledUsedByField>;
}

const FilterSearchField = ({ name, value, onChange, onReset }) => {
  return <label style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ marginRight: '4px' }}>Search:</span>
    <NekoInput name={name} value={value} onChange={onChange} onReset={onReset} />
  </label>;
}

const limitSelectElement = (name, value, onChange) => {
  return (
    <NekoSelect name={name} scrolldown={true} value={value} onChange={onChange}>
      <NekoOption value={5} label="5 items per page" />
      <NekoOption value={10} label="10 items per page" />
      <NekoOption value={25} label="25 items per page" />
      <NekoOption value={50} label="50 items per page" />
      <NekoOption value={100} label="100 items per page" />
      <NekoOption value={200} label="200 items per page" />
    </NekoSelect>
  );
}

const ItemInfoIcon = ({ infoType, info }) => {
  return (
    <NekoTooltip text={info}><NekoIcon icon="info-outline" width={16} height={16} color={infoType === 'warn' ? NekoTheme.orange : NekoTheme.black} style={{ marginLeft: '2px' }} /></NekoTooltip>
  );
}

export { BulkUsedByButton, UsedByField, FilterSearchField, NameField, ItemStatusIcon,
  SectionButtonStyle, SectionFooterStyle, limitSelectElement, ItemInfoIcon }